import styled from 'styled-components';

import { Rarity, Sport } from '@sorare/core/src/__generated__/globalTypes';
import { FullViewportScroll } from '@sorare/core/src/atoms/container/FullViewportScroll';
import { StarBall } from '@sorare/core/src/atoms/icons/StarBall';
import { Tab, TabBar } from '@sorare/core/src/atoms/navigation/TabBar';
import { LIFECYCLE, useLifecycle } from '@sorare/core/src/hooks/useLifecycle';
import { useSo5Competitions } from '@sorare/core/src/hooks/useSo5Competitions';

import { SelectRarity } from 'atoms/SelectRarity';

const StyledTabBar = styled(TabBar)`
  --active-fg-color: var(--c-white);
  --active-bg-color: var(--c-nd-200);
  width: 100%;
`;
const AUTHORIZED_RARITIES = [
  Rarity.limited,
  Rarity.rare,
  Rarity.super_rare,
  Rarity.unique,
];

const Root = styled.div`
  display: grid;
  max-width: 100%;
  grid-template-columns: 74px 1fr;
  gap: 0;
  padding: var(--double-unit) 0;
  position: sticky;
  top: var(--current-stack-height);
  z-index: 1;
  background: var(--c-black);
`;

const RarityFilter = styled.div`
  position: relative;
  isolation: isolate;
  z-index: 1;
  background: var(--c-black);
  padding-right: var(--unit);
`;
const CompetitionLogo = styled.img`
  height: 22px;
  min-width: 56px;
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;
const StyledStarBall = styled(StarBall)`
  height: 22px;
  min-width: 56px;
  .selected & {
    filter: grayscale(1) invert(1);
  }
`;
const StyledTab = styled(Tab)`
  flex-grow: 1 !important;
`;

export const Filters = ({ sport }: { sport: Sport }) => {
  const { scoutableSo5Competitions } = useSo5Competitions();

  const { lifecycle, update: updateLifecycle } = useLifecycle();
  const rarity =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.rarity || Rarity.limited;
  const competitionSlug =
    lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport]?.competitionSlug || '';

  const options = [
    {
      label: <StyledStarBall height={22} />,
      value: 'DEFAULT',
    },
    ...scoutableSo5Competitions.map(competition => ({
      label: (
        <CompetitionLogo
          src={competition.iconUrl}
          alt={competition.displayName}
        />
      ),
      value: competition.slug,
    })),
  ];

  const onChangeRarity = (r: Rarity) => {
    updateLifecycle(LIFECYCLE.marketHomeFilters, {
      [sport]: {
        ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
        rarity: r,
      },
    });
  };
  const onChangeCompetition = (c: string) => {
    updateLifecycle(LIFECYCLE.marketHomeFilters, {
      [sport]: {
        ...lifecycle?.[LIFECYCLE.marketHomeFilters]?.[sport],
        competitionSlug: c,
      },
    });
  };

  const hideCompetitionFilter = scoutableSo5Competitions.length < 2;

  return (
    <Root>
      <RarityFilter>
        <SelectRarity
          rarities={AUTHORIZED_RARITIES}
          selectedRarity={rarity}
          onChange={onChangeRarity}
        />
      </RarityFilter>

      {!hideCompetitionFilter && (
        <FullViewportScroll>
          <StyledTabBar
            fullWidth
            value={competitionSlug}
            variant="segmented"
            hideBackgroundWhenNoValue
            scrollable
            onValueChange={onChangeCompetition}
          >
            {options.map(option => (
              <StyledTab
                key={option.value}
                value={option.value}
                className={competitionSlug === option.value ? 'selected' : ''}
              >
                {option.label}
              </StyledTab>
            ))}
          </StyledTabBar>
        </FullViewportScroll>
      )}
    </Root>
  );
};
